import React, { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Collapse, Space, Switch, Tooltip, Typography } from 'antd';
import { RobotOutlined } from '@ant-design/icons';
import axios from '../../utils/axios';

const { Text, Paragraph } = Typography;
const { Panel } = Collapse;

// Per-feature session cache so switching pages doesn't re-fetch static
// prompt content once we've actually heard back from the server. A request
// that errors (network blip, transient 5xx) is NOT cached — caching it would
// permanently hide the button for the rest of the session even after the
// backend recovers, indistinguishable from the tool group being disabled.
const _promptsCache = new Map();

function fetchPrompts(feature) {
  if (!_promptsCache.has(feature)) {
    _promptsCache.set(
      feature,
      axios.get('/ai/prompts', { params: { feature } })
        .then((response) => response.data)
        .catch((err) => {
          _promptsCache.delete(feature);
          throw err;
        }),
    );
  }
  return _promptsCache.get(feature);
}

function substitutePlaceholders(template, contextFields, context, includedKeys) {
  let text = template;
  for (const field of contextFields) {
    const value = context[field.key];
    const included = includedKeys.has(field.key) && value != null && value !== '';
    const replacement = included
      ? String(value)
      : `(ask the user which ${field.label.toLowerCase()} to use)`;
    text = text.split(`{{${field.key}}}`).join(replacement);
  }
  return text;
}

function mcpConfigSnippet(mcpServerUrl) {
  return JSON.stringify(
    { mcpServers: { touchflow: { url: mcpServerUrl, headers: { Authorization: 'Bearer tfmcp_...' } } } },
    null,
    2,
  );
}

function availableFieldsFor(variant, context) {
  return variant.context_fields.filter((f) => context[f.key] != null && context[f.key] !== '');
}

function PromptVariantBody({ variant, context, includedKeys, onToggleField }) {
  const availableFields = availableFieldsFor(variant, context);
  const renderedPrompt = substitutePlaceholders(variant.body_template, variant.context_fields, context, includedKeys);

  return (
    <>
      <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>{variant.description}</Text>
      {availableFields.length > 0 && (
        <Space wrap style={{ marginBottom: 8 }}>
          {availableFields.map((field) => (
            <Space key={field.key} size={4}>
              <Switch
                size="small"
                checked={includedKeys.has(field.key)}
                onChange={() => onToggleField(field.key)}
              />
              <Text style={{ fontSize: 12 }}>
                Include {field.label.toLowerCase()} ({context[field.key]})
              </Text>
            </Space>
          ))}
        </Space>
      )}
      <Paragraph copyable={{ text: renderedPrompt }} code style={{ whiteSpace: 'pre-wrap', marginBottom: 0 }}>
        {renderedPrompt}
      </Paragraph>
    </>
  );
}

/**
 * Drop-in "Use with AI agent" button: expands into copyable prompts for an
 * external MCP-connected agent (Cursor, Claude Code/Desktop), scoped to one
 * MCP tool group ("feature") and optionally seeded with the current page's
 * context (e.g. { job_id, insight_id }).
 */
export default function AgentPromptButton({ feature, context = {}, buttonSize = 'middle' }) {
  const [expanded, setExpanded] = useState(false);
  const [prompts, setPrompts] = useState(null);
  const [loading, setLoading] = useState(false);
  // variant.id -> Set(context field keys currently included in that prompt's text)
  const [includedByVariant, setIncludedByVariant] = useState({});

  const includedKeysFor = (variant) =>
    includedByVariant[variant.id] || new Set(availableFieldsFor(variant, context).map((f) => f.key));

  const toggleField = (variant, key) => {
    setIncludedByVariant((prev) => {
      const next = new Set(prev[variant.id] || includedKeysFor(variant));
      if (next.has(key)) next.delete(key); else next.add(key);
      return { ...prev, [variant.id]: next };
    });
  };

  const handleToggle = useCallback(() => {
    if (!expanded && prompts === null && !loading) {
      setLoading(true);
      fetchPrompts(feature)
        .then((data) => setPrompts(data))
        .catch(() => {
          // Leave prompts as null (not an empty result) so a later click
          // retries instead of looking disabled; collapse back down since
          // there's nothing loaded to show.
          setExpanded(false);
        })
        .finally(() => setLoading(false));
    }
    setExpanded((prev) => !prev);
  }, [expanded, prompts, loading, feature]);

  // Once loaded, hide the whole feature entirely when there's nothing to
  // show (tool group disabled, or the fetch failed) — never dangle a button
  // that opens onto an empty panel.
  if (prompts !== null && prompts.variants.length === 0) {
    return null;
  }

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <Tooltip title="Use with AI agent">
        <Button icon={<RobotOutlined />} size={buttonSize} loading={loading} onClick={handleToggle}>
          AI
        </Button>
      </Tooltip>
      {expanded && prompts && prompts.variants.length > 0 && (
        <>
          <div
            onClick={() => setExpanded(false)}
            style={{ position: 'fixed', inset: 0, zIndex: 999, background: 'rgba(0, 0, 0, 0.25)' }}
          />
          <div
            style={{
              position: 'fixed', top: '15vh', left: '50%', transform: 'translateX(-50%)',
              textAlign: 'left', zIndex: 1000, width: 720, maxWidth: '90vw',
              maxHeight: '75vh', overflowY: 'auto',
              background: 'var(--bg-primary, #fff)', borderRadius: 8,
              boxShadow: '0 8px 32px var(--shadow-light, rgba(0,0,0,0.24))',
            }}
          >
            <Collapse>
              {prompts.variants.map((variant) => (
                <Panel header={variant.title} key={variant.id}>
                  <PromptVariantBody
                    variant={variant}
                    context={context}
                    includedKeys={includedKeysFor(variant)}
                    onToggleField={(key) => toggleField(variant, key)}
                  />
                </Panel>
              ))}
              <Panel header="Connect your agent" key="__mcp_config">
                <Text strong style={{ display: 'block', marginBottom: 4 }}>MCP server URL</Text>
                <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                  Add this as an MCP server in Cursor, Claude Code, or Claude Desktop, then sign in
                  when prompted — supported clients use OAuth, so no token needed.
                </Text>
                <Paragraph copyable={{ text: prompts.mcp_server_url }} code style={{ fontSize: 15, marginBottom: 12 }}>
                  {prompts.mcp_server_url}
                </Paragraph>
                <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 12 }}>
                  Full walkthrough: <Link to="/docs/mcp-setup">connecting an AI agent →</Link>
                </Text>
                <Collapse ghost size="small">
                  <Panel header={<Text type="secondary" style={{ fontSize: 12 }}>Can't use OAuth? Manual token (legacy)</Text>} key="__manual">
                    <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                      For headless or CI clients only. Create a manual token from{' '}
                      <Link to="/mcp-tokens">MCP API Tokens</Link> and substitute it below.
                    </Text>
                    <Paragraph copyable={{ text: mcpConfigSnippet(prompts.mcp_server_url) }} code style={{ whiteSpace: 'pre-wrap', marginBottom: 0 }}>
                      {mcpConfigSnippet(prompts.mcp_server_url)}
                    </Paragraph>
                  </Panel>
                </Collapse>
              </Panel>
            </Collapse>
          </div>
        </>
      )}
    </div>
  );
}
